import { publishCollectionChange } from '../utils/collectionSync';
import {
  createOfflineUuid,
  enqueuePendingWrite,
  isOffline,
} from './offlineMutationQueue';
import { getSharedRecords, mergeRecordsByUuid, removeSharedRecord, upsertSharedRecord, upsertSharedRecords } from './sharedRecordCache';
import { resolveUuidAlias } from '../offline/db';
import { filterItemsByCurrentScope } from './viewerAccess';

function canonicalKey(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return resolveUuidAlias(raw) ?? raw;
}

function findSharedRecord(collectionKey, uuid) {
  const target = canonicalKey(uuid);
  if (!target) return null;

  return getSharedRecords(collectionKey).find((record) => {
    const key = canonicalKey(record?.uuid ?? record?.id ?? '');
    return key === target;
  }) ?? null;
}

function buildOptimisticRecord({ normalize, buildPayload, payload, uuid, existing }) {
  const now = new Date().toISOString();
  const rawPayload = typeof buildPayload === 'function' ? buildPayload(payload) : (payload ?? {});
  const base = existing && typeof existing === 'object' ? existing : {};
  const raw = {
    ...base,
    ...rawPayload,
    uuid,
    id: uuid,
    created_at: base.created_at ?? rawPayload.created_at ?? now,
    createdAt: base.createdAt ?? rawPayload.createdAt ?? now,
    updated_at: now,
    updatedAt: now,
    sync_status: 'pending',
    syncStatus: 'pending',
  };

  return typeof normalize === 'function' ? normalize(raw) : raw;
}

function applyOptimisticAction(record, action, notes = '') {
  const next = { ...(record ?? {}) };
  const statusMap = {
    submit: 'submitted',
    validate: 'validated',
    approve: 'approved',
    reject: 'rejected',
  };
  const status = statusMap[action] ?? null;

  if (status) {
    if ('data_status' in next || 'dataStatus' in next || next.data_status !== undefined || next.dataStatus !== undefined) {
      next.data_status = status;
      next.dataStatus = status;
    }
    if ('validation_status' in next || 'validationStatus' in next || next.validation_status !== undefined || next.validationStatus !== undefined) {
      next.validation_status = status;
      next.validationStatus = status;
    }
    if ('workflow_status' in next || 'workflowStatus' in next || next.workflow_status !== undefined || next.workflowStatus !== undefined) {
      next.workflow_status = status;
      next.workflowStatus = status;
    }
    if ('status' in next || next.status !== undefined) {
      next.status = status;
    }
  }

  if (action === 'reject') {
    const rejectionNotes = String(notes ?? '').trim();
    if (rejectionNotes) {
      next.rejection_notes = rejectionNotes;
      next.rejectionNotes = rejectionNotes;
    }
  }

  return next;
}

function queueMutation({ collectionKey, action, method, url, data, tempUuid, recordUuid }) {
  enqueuePendingWrite({
    collectionKey,
    action,
    method,
    url,
    data,
    tempUuid,
    recordUuid,
  });
}

function normaliseListResponse(liveResponse, collectionKey, normalize) {
  const liveItems = Array.isArray(liveResponse?.items)
    ? liveResponse.items
    : Array.isArray(liveResponse)
      ? liveResponse
      : [];
  const cached = getSharedRecords(collectionKey);
  if (liveItems.length > 0) {
    upsertSharedRecords(collectionKey, liveItems);
  }
  return {
    ...(liveResponse && typeof liveResponse === 'object' ? liveResponse : {}),
    // The cache is shared by every login in this browser (e.g. an earlier R01
    // session), so the merged list is re-scoped to the current viewer.
    items: filterItemsByCurrentScope(mergeRecordsByUuid(liveItems.map((item) => (typeof normalize === 'function' ? normalize(item) : item)), cached)),
  };
}

function normaliseListAllResponse(liveResponse, collectionKey, normalize) {
  const liveItems = Array.isArray(liveResponse?.items)
    ? liveResponse.items
    : Array.isArray(liveResponse)
      ? liveResponse
      : [];
  const cached = getSharedRecords(collectionKey);
  if (liveItems.length > 0) {
    upsertSharedRecords(collectionKey, liveItems);
  }
  return {
    ...(liveResponse && typeof liveResponse === 'object' ? liveResponse : {}),
    // The cache is shared by every login in this browser (e.g. an earlier R01
    // session), so the merged list is re-scoped to the current viewer.
    items: filterItemsByCurrentScope(mergeRecordsByUuid(liveItems.map((item) => (typeof normalize === 'function' ? normalize(item) : item)), cached)),
  };
}

export function withOfflineCollectionSupport(baseApi, options = {}) {
  const {
    collectionKey = baseApi.collectionKey ?? null,
    basePath,
    normalize,
    buildPayload,
  } = options;

  if (!collectionKey) {
    throw new Error('withOfflineCollectionSupport requires a collectionKey.');
  }

  if (!basePath) {
    throw new Error(`withOfflineCollectionSupport(${collectionKey}) requires a basePath.`);
  }

  const liveBaseApi = baseApi ?? {};

  async function handleAction(action, uuid, extra = {}) {
    const methodName = action;
    const liveMethod = liveBaseApi[methodName];
    if (typeof liveMethod !== 'function') {
      throw new Error(`Collection API for ${collectionKey} does not implement ${methodName}().`);
    }

    if (isOffline()) {
      const existing = findSharedRecord(collectionKey, uuid);
      const optimistic = applyOptimisticAction(
        buildOptimisticRecord({
          normalize,
          buildPayload,
          payload: existing ?? { uuid, id: uuid },
          uuid,
          existing,
        }),
        action,
        extra.notes ?? ''
      );

      if (action === 'remove') {
        removeSharedRecord(collectionKey, uuid);
      } else {
        upsertSharedRecord(collectionKey, optimistic);
      }

      const queuedData = extra.data ?? (action === 'reject' ? { notes: String(extra.notes ?? '').trim() } : null);
      queueMutation({
        collectionKey,
        action,
        method: 'post',
        url: `${basePath}/${uuid}/${action}`,
        data: queuedData,
        tempUuid: extra.tempUuid ?? null,
        recordUuid: uuid,
      });

      publishCollectionChange(collectionKey, {
        action,
        uuid,
        offline: true,
      });

      return action === 'remove' ? { uuid, removed: true, offline: true } : optimistic;
    }

    const liveArgs = extra.args ?? (action === 'reject' ? [extra.notes ?? ''] : []);
    const result = await liveMethod.call(liveBaseApi, uuid, ...liveArgs);
    if (action === 'remove') {
      removeSharedRecord(collectionKey, uuid);
    } else if (result?.uuid || result?.id) {
      upsertSharedRecord(collectionKey, result);
    }

    publishCollectionChange(collectionKey, { action, uuid });
    return result;
  }

  return {
    ...liveBaseApi,
    collectionKey,

    async list(params = {}) {
      try {
        const live = await liveBaseApi.list(params);
        return normaliseListResponse(live, collectionKey, normalize);
      } catch (error) {
        if (isOffline()) {
          const cached = getSharedRecords(collectionKey);
          return {
            items: filterItemsByCurrentScope(cached.map((record) => (typeof normalize === 'function' ? normalize(record) : record))),
            pagination: null,
          };
        }
        throw error;
      }
    },

    async listAll(params = {}) {
      try {
        const live = await liveBaseApi.listAll(params);
        return normaliseListAllResponse(live, collectionKey, normalize);
      } catch (error) {
        if (isOffline()) {
          const cached = getSharedRecords(collectionKey);
          return {
            items: filterItemsByCurrentScope(cached.map((record) => (typeof normalize === 'function' ? normalize(record) : record))),
            pagination: null,
          };
        }
        throw error;
      }
    },

    async get(uuid) {
      try {
        const record = await liveBaseApi.get(uuid);
        if (record?.uuid || record?.id) {
          upsertSharedRecord(collectionKey, record);
        }
        return record;
      } catch (error) {
        if (isOffline()) {
          const cached = findSharedRecord(collectionKey, uuid);
          if (cached) return typeof normalize === 'function' ? normalize(cached) : cached;
          return typeof normalize === 'function' ? normalize({ uuid, id: uuid }) : { uuid, id: uuid };
        }
        throw error;
      }
    },

    async create(payload) {
      if (isOffline()) {
        const tempUuid = createOfflineUuid(collectionKey);
        const optimistic = buildOptimisticRecord({
          normalize,
          buildPayload,
          payload,
          uuid: tempUuid,
          existing: null,
        });
        upsertSharedRecord(collectionKey, optimistic);
        queueMutation({
          collectionKey,
          action: 'create',
          method: 'post',
          url: basePath,
          data: typeof buildPayload === 'function' ? buildPayload(payload) : payload,
          tempUuid,
          recordUuid: tempUuid,
        });
        publishCollectionChange(collectionKey, { action: 'create', uuid: tempUuid, offline: true });
        return optimistic;
      }

      const record = await liveBaseApi.create(payload);
      if (record?.uuid || record?.id) {
        upsertSharedRecord(collectionKey, record);
      }
      publishCollectionChange(collectionKey, { action: 'create', uuid: record?.uuid ?? record?.id ?? null });
      return record;
    },

    async update(uuid, payload) {
      if (isOffline()) {
        const existing = findSharedRecord(collectionKey, uuid);
        const optimistic = buildOptimisticRecord({
          normalize,
          buildPayload,
          payload,
          uuid,
          existing,
        });
        upsertSharedRecord(collectionKey, optimistic);
        queueMutation({
          collectionKey,
          action: 'update',
          method: 'put',
          url: `${basePath}/${uuid}`,
          data: typeof buildPayload === 'function' ? buildPayload(payload) : payload,
          recordUuid: uuid,
        });
        publishCollectionChange(collectionKey, { action: 'update', uuid, offline: true });
        return optimistic;
      }

      const record = await liveBaseApi.update(uuid, payload);
      if (record?.uuid || record?.id) {
        upsertSharedRecord(collectionKey, record);
      }
      publishCollectionChange(collectionKey, { action: 'update', uuid });
      return record;
    },

    async remove(uuid) {
      if (isOffline()) {
        removeSharedRecord(collectionKey, uuid);
        queueMutation({
          collectionKey,
          action: 'remove',
          method: 'delete',
          url: `${basePath}/${uuid}`,
          recordUuid: uuid,
        });
        publishCollectionChange(collectionKey, { action: 'remove', uuid, offline: true });
        return { uuid, removed: true, offline: true };
      }

      const result = await liveBaseApi.remove(uuid);
      removeSharedRecord(collectionKey, uuid);
      publishCollectionChange(collectionKey, { action: 'remove', uuid });
      return result;
    },

    async submit(uuid) {
      return handleAction('submit', uuid);
    },

    async validate(uuid) {
      return handleAction('validate', uuid);
    },

    async approve(uuid) {
      return handleAction('approve', uuid);
    },

    async reject(uuid, notes = '') {
      return handleAction('reject', uuid, { notes });
    },
  };
}
